/* Phase B v2 — Sizing AI cart attribute attacher
 *
 * Attaches `_apolla_rec_id` (rec UUID from localStorage.apolla_pending_rec_id)
 * to add-to-cart events so the Shopify carts/update webhook can correlate
 * cart contents back to the sizing AI recommendation.
 *
 * v1 (DEPRECATED) wrote a cart-LEVEL attribute via /cart/update.js after the
 * add fired. Discovered 2026-05-12 that Shopify cart webhook payloads
 * (carts/create, carts/update) EXCLUDE cart-level attributes — the payload
 * only includes id/token/line_items/note/updated_at/created_at. So that
 * mechanism never delivered the rec_id to the webhook handler.
 *
 * v2 (THIS FILE) injects `_apolla_rec_id` as a LINE ITEM PROPERTY at
 * /cart/add time. Line item properties ARE included in cart/checkout/order
 * webhooks under line_items[].properties — verified per Shopify cart resource
 * schema. Also keeps the v1 cart-attribute call as fallback (cheap, harmless,
 * future-proofs against any platform path that exposes attributes).
 *
 * Covers 4 ATC surfaces:
 *   1. <form action="/cart/add"> traditional submit  → inject hidden input
 *   2. fetch('/cart/add(.js)?') JSON body            → parse / inject / restringify
 *   3. fetch('/cart/add(.js)?') FormData body        → append('properties[_apolla_rec_id]')
 *   4. fetch('/cart/add(.js)?') URLSearchParams body → append
 *
 * XMLHttpRequest path NOT body-mutated (rare/legacy) but cart-attribute
 * fallback still fires for those.
 *
 * Fire-and-forget: never blocks the user's add-to-cart flow.
 *
 * Sentinel: APOLLA_SIZING_CART_ATTRIBUTE_v2 — search before editing other
 * theme JS to avoid duplicate listeners.
 */
(function () {
  // Phase A gate (added 2026-05-18): page-scope the fetch/XHR wrappers
  // to PDP / cart / collection routes OR when a pending rec_id exists.
  // Sitewide install was adding ~50-80ms TBT on irrelevant pages.
  var __apolla_path = window.location.pathname;
  var __apolla_relevant = /^\/(products\/|cart|collections\/)/.test(__apolla_path);
  var __apolla_pending = false;
  try { __apolla_pending = !!localStorage.getItem('apolla_pending_rec_id'); }
  catch (e) { __apolla_pending = true; /* fail-open if localStorage blocked */ }
  if (!__apolla_relevant && !__apolla_pending) { return; }

  if (window.__apollaSizingCartAttribute_v2) return;
  window.__apollaSizingCartAttribute_v2 = true;

  var REC_ID_KEY = '_apolla_rec_id';
  var FORM_FIELD_NAME = 'properties[' + REC_ID_KEY + ']';
  var ADD_PATH_RE = /\/cart\/add(?:\.js)?(?:[?#]|$)/;

  function getPendingRecId() {
    try {
      var v = localStorage.getItem('apolla_pending_rec_id');
      if (!v) return null;
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return null;
      return v;
    } catch (e) { return null; }
  }

  // Fallback: also write the rec_id as a cart-level attribute. Harmless if
  // webhook never sees it; helps if Apolla ever switches to checkout/order
  // webhooks (which DO surface cart attributes via note_attributes).
  function attachCartAttribute(recId) {
    if (!recId) return;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _apolla_rec_id: recId } }),
        keepalive: true
      }).catch(function () { /* swallow */ });
    } catch (e) { /* swallow */ }
  }

  // Inject the rec_id property into a fetch body. Returns the new body, or
  // the original body if injection wasn't possible. Never throws.
  function injectIntoBody(body, recId) {
    if (!recId) return body;
    try {
      // FormData
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        // Don't double-inject if already present
        if (!body.has(FORM_FIELD_NAME)) body.append(FORM_FIELD_NAME, recId);
        return body;
      }
      // URLSearchParams
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        if (!body.has(FORM_FIELD_NAME)) body.append(FORM_FIELD_NAME, recId);
        return body;
      }
      // String — try JSON first (Shopify /cart/add.js takes JSON)
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
          try {
            var obj = JSON.parse(body);
            // Shopify /cart/add.js accepts either a single line item or { items: [...] }
            if (Array.isArray(obj.items)) {
              obj.items = obj.items.map(function (it) {
                if (!it || typeof it !== 'object') return it;
                var props = (it.properties && typeof it.properties === 'object') ? it.properties : {};
                if (!props[REC_ID_KEY]) props[REC_ID_KEY] = recId;
                it.properties = props;
                return it;
              });
            } else if (obj && typeof obj === 'object') {
              var props2 = (obj.properties && typeof obj.properties === 'object') ? obj.properties : {};
              if (!props2[REC_ID_KEY]) props2[REC_ID_KEY] = recId;
              obj.properties = props2;
            }
            return JSON.stringify(obj);
          } catch (e) { /* fall through to URL-encoded path */ }
        }
        // URL-encoded fallback
        try {
          var params = new URLSearchParams(body);
          if (!params.has(FORM_FIELD_NAME)) params.append(FORM_FIELD_NAME, recId);
          return params.toString();
        } catch (e2) { return body; }
      }
      return body;
    } catch (e) { return body; }
  }

  // 1. Traditional form submit to /cart/add — inject hidden input pre-submit
  document.addEventListener('submit', function (ev) {
    try {
      var form = ev.target;
      if (!form || !form.action) return;
      if (!ADD_PATH_RE.test(form.action)) return;
      var recId = getPendingRecId();
      if (!recId) return;
      // Don't double-inject
      if (!form.querySelector('input[name="' + FORM_FIELD_NAME + '"]')) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = FORM_FIELD_NAME;
        input.value = recId;
        form.appendChild(input);
      }
      attachCartAttribute(recId);
    } catch (e) { /* swallow */ }
  }, true);

  // 2. fetch() interception — mutate body before request goes out
  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (ADD_PATH_RE.test(url)) {
          var recId = getPendingRecId();
          if (recId) {
            attachCartAttribute(recId);
            // Mutate init.body when caller passed (url, init) form
            if (init && init.body != null) {
              init = Object.assign({}, init, { body: injectIntoBody(init.body, recId) });
            }
            // When caller passed a Request object, leave it alone (rare on
            // Shopify themes). Cart-attribute fallback still fires.
          }
        }
      } catch (e) { /* swallow */ }
      return origFetch.call(this, input, init);
    };
  }

  // 3. XMLHttpRequest interception — fallback to cart-attribute only
  // (mutating XHR.send body in-flight is brittle; modern themes use fetch)
  if (typeof window.XMLHttpRequest === 'function') {
    var origOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (typeof url === 'string' && ADD_PATH_RE.test(url)) {
          var recId = getPendingRecId();
          if (recId) attachCartAttribute(recId);
        }
      } catch (e) { /* swallow */ }
      return origOpen.apply(this, arguments);
    };
  }
})();